// PoImportReview — /procurement/import. ERP expansion chunk 1, phase 9 (the
// final Deploy C build): review screen for the historical purchase-order
// import. Mannie reviews ~193 real documents through this screen personally,
// so every avoidable click here multiplies by 193 — see the vendor triage
// panel in particular, which exists specifically to turn ~50 vendor-type
// picks into ~15.
//
// parse() writes NOTHING (po-import-router.js) — everything below is a
// proposal held in local state until Confirm. Backend contract: read
// api/src/procurement/po-import-router.js + po-import-service.js before
// changing anything here; poImportApi.ts mirrors it exactly.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import { Alert } from '../components/ui/Alert';
import { Appear } from '../components/ui/Appear';
import FadeIn from '../components/ui/FadeIn';
import Reveal from '../components/ui/Reveal';
import { EmptyState } from '../components/ui/EmptyState';
import { LogoSpinner } from '../components/ui/LogoSpinner';
import { Panel } from '../components/ui/Panel';
import HelpDrawer from '../components/HelpDrawer';
import PageLayout from '../components/ui/PageLayout';
import { buttonClass, IconButton } from '../components/ui/Button';
import Tooltip from '../components/ui/Tooltip';
import DropZone from '../components/ui/DropZone';
import { SegTrack } from '../components/ui/SegTrack';
import { IconClose } from '../components/ui/icons';
import { usePoImportApi, PRODUCT_LABEL, type ImportLine, type ParseResponse } from './poImportApi';
import {
  buildInitialDrafts,
  buildConfirmInput,
  computeBlockingReasons,
  computeVendorGroups,
  defaultGroupResolution,
  groupsByDocKey,
  patchLineInDraft,
  resolveEffectiveVendor,
  unreconciledReviewKeys,
  importErrorMessage,
  importErrorPoNumber,
  type DocDraft,
  type VendorGroupResolution,
  type VendorOption,
} from './poImportDraft';
import type { VendorTypeOption } from './vendorTypeLabels';
import PoImportDocumentCard from './PoImportDocumentCard';
import PoImportVendorTriage from './PoImportVendorTriage';
import VendorPreviewModal, { type VendorPreviewSubject } from './VendorPreviewModal';

type Phase = 'upload' | 'review';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const dedupeFiles = (existing: File[], incoming: FileList | File[]): File[] => {
  const seen = new Set(existing.map((f) => `${f.name}:${f.size}`));
  const merged = [...existing];
  for (const f of Array.from(incoming)) {
    if (!/\.(xlsx|pdf)$/i.test(f.name)) continue;
    const key = `${f.name}:${f.size}`;
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(f);
  }
  return merged;
};

const PoImportReview: React.FC = () => {
  const api = useApi();
  const poImportApi = usePoImportApi();
  const toast = useToast();

  const [phase, setPhase] = useState<Phase>('upload');

  // --- upload step ---
  const [files, setFiles] = useState<File[]>([]);
  const [workbookFile, setWorkbookFile] = useState<File | null>(null);
  const [parsing, setParsing] = useState(false);

  // --- parsed batch ---
  const [parseResponse, setParseResponse] = useState<ParseResponse | null>(null);
  const [wallTimeMs, setWallTimeMs] = useState(0);
  const [showNotImported, setShowNotImported] = useState(false);

  // --- working drafts ---
  const [order, setOrder] = useState<string[]>([]);
  const [drafts, setDrafts] = useState<Record<string, DocDraft>>({});
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState<'all' | 'attention' | 'warnings'>('all');

  // --- vendor picker data + group resolutions ---
  const [vendorList, setVendorList] = useState<VendorOption[]>([]);
  const [vendorTypes, setVendorTypes] = useState<VendorTypeOption[]>([]);
  const [resolutions, setResolutions] = useState<Record<string, VendorGroupResolution>>({});

  // --- confirm ---
  const [confirming, setConfirming] = useState(false);
  const [confirmResult, setConfirmResult] = useState<{
    created: { po_number: string; purchase_order_id: string }[];
    skipped: number;
    attached: number;
  } | null>(null);
  const [droppedAtConfirm, setDroppedAtConfirm] = useState(0);

  // --- vendor preview ---
  const [previewSubject, setPreviewSubject] = useState<VendorPreviewSubject | null>(null);

  const vendorTriageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    (async () => {
      try {
        const data = (await api.get('/procurement/vendors?includeDisabled=1')) as {
          vendors: { id: string; name: string; type: string; is_disabled: boolean }[];
          // The taxonomy arrives with its labels, active only and in order — an
          // admin edits it at /admin/vendor-types, so a key-to-label map on this
          // side would serve the old name after a rename.
          types: VendorTypeOption[];
        };
        setVendorList(
          (data.vendors || []).map((v) => ({ id: v.id, name: v.name, type: v.type, is_disabled: v.is_disabled }))
        );
        setVendorTypes(data.types || []);
      } catch {
        // The vendor triage panel degrades to "create only, no existing-vendor
        // matches offered" without this — not fatal, matches the vendor-filter
        // fallback precedent in PurchaseOrderList.tsx.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The paper the cards show, joined by filename. The uploaded File objects
  // survive in `files` for the whole review phase, so the viewer needs no
  // upload, no proxy and no storage: it reads the very bytes the reviewer
  // dropped, and they die with the batch (start over clears them, a reload
  // loses them). Name alone is the key because `doc.source_filename` is the
  // only handle the parse response carries; dedupeFiles keys on name+size, so
  // two same-named files of different sizes in one batch collapse to the last
  // one here, which is the honest best a filename join can do.
  const filesByName = useMemo(() => {
    const map = new Map<string, File>();
    for (const f of files) map.set(f.name, f);
    return map;
  }, [files]);

  const draftList = useMemo(() => order.map((k) => drafts[k]).filter(Boolean), [order, drafts]);
  const includedDrafts = useMemo(() => draftList.filter((d) => d.include), [draftList]);

  const groups = useMemo(() => computeVendorGroups(includedDrafts, vendorList), [includedDrafts, vendorList]);
  const groupByKey = useMemo(() => groupsByDocKey(groups), [groups]);
  const resolvedResolutions = useMemo(() => {
    const map: Record<string, VendorGroupResolution> = {};
    for (const g of groups) map[g.groupKey] = resolutions[g.groupKey] || defaultGroupResolution(g);
    return map;
  }, [groups, resolutions]);

  const effectiveVendorByKey = useMemo(() => {
    const map = new Map<string, ReturnType<typeof resolveEffectiveVendor>>();
    for (const d of draftList) map.set(d.key, resolveEffectiveVendor(d, groupByKey, resolvedResolutions));
    return map;
  }, [draftList, groupByKey, resolvedResolutions]);

  const blockingByKey = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const d of draftList) {
      const effective = effectiveVendorByKey.get(d.key)!;
      map.set(d.key, computeBlockingReasons(d, effective));
    }
    return map;
  }, [draftList, effectiveVendorByKey]);

  const readyToConfirmCount = useMemo(
    () => includedDrafts.filter((d) => (blockingByKey.get(d.key) || []).length === 0).length,
    [includedDrafts, blockingByKey]
  );

  // Counted apart from the rest of "needs attention" on purpose: a missing
  // vendor is a decision the reviewer has not made yet, while this is a
  // decision the READER could not make and handed over. They are different
  // kinds of work and the batch bar should not blur them together.
  const awaitingReviewCount = useMemo(
    () => includedDrafts.filter((d) => unreconciledReviewKeys(d).length > 0).length,
    [includedDrafts]
  );

  const patchDraft = (key: string, patch: Partial<DocDraft>) => {
    setDrafts((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  };

  const patchLine = (key: string, index: number, patch: Partial<ImportLine>) => {
    setDrafts((prev) => {
      const d = prev[key];
      if (!d) return prev;
      // patchLineInDraft, not a local map: including or excluding a line has to
      // carry the header subtotal with it (poImportDraft.ts).
      return { ...prev, [key]: patchLineInDraft(d, index, patch) };
    });
  };

  const toggleExpand = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleChangeResolution = (groupKey: string, patch: Partial<VendorGroupResolution>) => {
    setResolutions((prev) => {
      const group = groups.find((g) => g.groupKey === groupKey);
      const current = prev[groupKey] || (group ? defaultGroupResolution(group) : null);
      if (!current) return prev;
      return { ...prev, [groupKey]: { ...current, ...patch } };
    });
  };

  const jumpToVendorPanel = () => {
    vendorTriageRef.current?.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
  };

  // ---------------------------------------------------------------------
  // Upload → parse
  // ---------------------------------------------------------------------

  const handleParse = async () => {
    if (files.length === 0) return;
    setParsing(true);
    const t0 = performance.now();
    try {
      const res = await poImportApi.parse(files, workbookFile);
      setWallTimeMs(performance.now() - t0);
      setParseResponse(res);
      const initial = buildInitialDrafts(res.documents);
      const map: Record<string, DocDraft> = {};
      for (const d of initial) map[d.key] = d;
      setDrafts(map);
      setOrder(initial.map((d) => d.key));
      setResolutions({});
      setExpanded(new Set());
      setConfirmResult(null);
      setPhase('review');
    } catch (err: any) {
      toast.error(importErrorMessage(err));
    } finally {
      setParsing(false);
    }
  };

  // ---------------------------------------------------------------------
  // Confirm
  // ---------------------------------------------------------------------

  const handleConfirm = async () => {
    if (includedDrafts.length === 0) {
      toast.error('Nothing is checked to confirm.');
      return;
    }
    const stillBlocked = includedDrafts.filter((d) => (blockingByKey.get(d.key) || []).length > 0);
    if (stillBlocked.length > 0) {
      toast.error(
        `${stillBlocked.length} checked document${stillBlocked.length === 1 ? ' still needs' : 's still need'} attention: ${stillBlocked
          .slice(0, 3)
          .map((d) => d.poNumber)
          .join(', ')}${stillBlocked.length > 3 ? '…' : ''}`
      );
      setExpanded((prev) => new Set(prev).add(stillBlocked[0].key));
      return;
    }
    const numberCounts = new Map<string, number>();
    for (const d of includedDrafts) numberCounts.set(d.poNumber, (numberCounts.get(d.poNumber) || 0) + 1);
    const dupe = Array.from(numberCounts.entries()).find(([, count]) => count > 1);
    if (dupe) {
      toast.error(`${dupe[0]} is checked on more than one document here. Uncheck all but one before confirming.`);
      return;
    }

    setConfirming(true);
    try {
      const payload = includedDrafts.map((d) => buildConfirmInput(d, effectiveVendorByKey.get(d.key)!, vendorList));
      const result = await poImportApi.confirm(payload);

      const confirmedKeys = new Set(includedDrafts.map((d) => d.key));
      setOrder((prev) => prev.filter((k) => !confirmedKeys.has(k)));
      setDrafts((prev) => {
        const next = { ...prev };
        confirmedKeys.forEach((k) => delete next[k]);
        return next;
      });
      setDroppedAtConfirm(order.length - includedDrafts.length);
      setConfirmResult({
        created: result.created.map((c) => ({ po_number: c.po_number, purchase_order_id: c.purchase_order_id })),
        skipped: result.skipped.length,
        attached: result.attached,
      });
      toast.success(
        `Imported ${result.created.length} purchase order${result.created.length === 1 ? '' : 's'}` +
          (result.attached ? `, attached ${result.attached} line${result.attached === 1 ? '' : 's'}` : '') +
          '.'
      );
    } catch (err: any) {
      toast.error(importErrorMessage(err));
      const poNum = importErrorPoNumber(err);
      if (poNum) {
        const hit = draftList.find((d) => d.poNumber === poNum);
        if (hit) setExpanded((prev) => new Set(prev).add(hit.key));
      }
    } finally {
      setConfirming(false);
    }
  };

  const handleNextBatch = () => {
    setPhase('upload');
    setFiles([]);
    setWorkbookFile(null);
    setParseResponse(null);
    setOrder([]);
    setDrafts({});
    setResolutions({});
    setExpanded(new Set());
    setConfirmResult(null);
    setDroppedAtConfirm(0);
  };

  // ---------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------

  const filteredList = useMemo(() => {
    if (filter === 'attention') {
      return draftList.filter((d) => d.include && (blockingByKey.get(d.key) || []).length > 0);
    }
    if (filter === 'warnings') {
      return draftList.filter((d) => (d.original.warnings || []).length > 0);
    }
    return draftList;
  }, [draftList, filter, blockingByKey]);

  const notImportedDocs = useMemo(
    () => (parseResponse ? parseResponse.documents.filter((d) => d.status !== 'ready') : []),
    [parseResponse]
  );

  return (
    <PageLayout width="wide" kind="workspace" className="pt-4 space-y-5">
      <div className="flex items-center gap-2">
        <h1 className="text-2xl font-bold text-primary">Import Purchase Order History</h1>
        <HelpDrawer title="About this screen">
          <p>
            This screen brings years of hand-made purchase orders into Firefleet, without guessing.
            Upload the spreadsheets (and the occasional PDF, for the rare order with no spreadsheet
            behind it) and this screen reads each one and shows you what it found. Nothing is saved
            anywhere until you press Confirm.
          </p>
          <p className="text-on-surface font-medium">Documents give structure, the corrections book gives truth</p>
          <p>
            The uploaded files supply the shape of each order: what was bought, from whom, how much.
            If a batch of these was already reconciled against real invoices (the corrections book,
            uploaded in its own slot), that reconciliation outranks the document itself, since an
            invoice sits closer to the truth than a hand-typed order form. Either way, you give the
            final word before anything is filed.
          </p>
          <p className="text-on-surface font-medium">What attaching means</p>
          <p>
            Some of these purchase orders were already tracked in the receiving system before this
            page existed, just without a proper header document behind them. Attaching links those
            existing records to the purchase order created here instead of creating duplicates.
            Whatever already arrived stays exactly as recorded, only the paperwork catches up.
          </p>
          <p className="text-on-surface font-medium">Why old orders default to Closed</p>
          <p>
            Every order on this screen already happened years ago, so there is no approval left to
            wait for and nothing genuinely pending. Closed is the honest default for history; change
            it if a specific order really is still open.
          </p>
          <p className="text-on-surface font-medium">The vendor triage panel</p>
          <p>
            When a document names a vendor Firefleet has not bought from before, this screen groups
            similar spellings together (Amazon, Amazon Business, and Amazon Business Account, for
            instance) so you pick one name and one type once for the whole group, instead of once
            per document.
          </p>
          <p className="text-on-surface font-medium">When the reading assistant is not sure</p>
          <p>
            A handful of these orders exist only as a PDF, so a reading assistant transcribes them
            instead of a spreadsheet being read directly. Anywhere it was unsure of what it read, the
            field is marked and waits for you: give it a look, then either correct it or say it looks
            right. A document with anything still waiting will not confirm, so nothing the assistant
            was unsure of can slip through unlooked-at.
          </p>
          <p className="text-on-surface font-medium">Nothing lands until Confirm</p>
          <p>
            Everything above is a proposal. Fix a wrong date, correct a vendor name, uncheck a line,
            or leave a whole document out, and none of it touches the database until you press
            Confirm on the batch. A rejected batch changes nothing, and can simply be retried.
          </p>
        </HelpDrawer>
      </div>

      {phase === 'upload' && (
        <FadeIn key="upload">
        <UploadStep
          files={files}
          workbookFile={workbookFile}
          parsing={parsing}
          onAddFiles={(incoming) => setFiles((prev) => dedupeFiles(prev, incoming))}
          onRemoveFile={(name) => setFiles((prev) => prev.filter((f) => f.name !== name))}
          onClearFiles={() => setFiles([])}
          onSetWorkbook={setWorkbookFile}
          onParse={handleParse}
        />
        </FadeIn>
      )}

      {phase === 'review' && parseResponse && (
        <FadeIn key="review" className="space-y-5">
          {/* Summary header */}
          <Panel label="Batch summary">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <div className="text-sm text-on-surface">
              <span className="text-green-300 font-semibold">{parseResponse.summary.ready}</span> ready ·{' '}
              <span className="text-amber-300 font-semibold">
                {parseResponse.documents.filter((d) => (d.warnings || []).length > 0).length}
              </span>{' '}
              with warnings ·{' '}
              <span className="text-on-surface-variant font-semibold">{parseResponse.summary.duplicates}</span> duplicates
              {parseResponse.summary.foreign_entity > 0 && (
                <>
                  {' · '}
                  <span className="text-on-surface font-semibold">{parseResponse.summary.foreign_entity}</span>{' '}
                  raised by {PRODUCT_LABEL[parseResponse.instance === 'swarm' ? 'firefly' : 'swarm']}
                </>
              )}
            </div>
            <div className="text-xs text-on-surface-variant ml-auto">
              {(wallTimeMs / 1000).toFixed(1)}s · importing into{' '}
              <span className="text-on-surface">{PRODUCT_LABEL[parseResponse.instance]}</span>{' '}
              ({parseResponse.instance_po_prefix})
            </div>
            {parseResponse.workbook.present && (
              <div className="w-full text-xs text-indigo-300">
                Corrections book: {parseResponse.workbook.counts?.corrections || 0} corrections,{' '}
                {parseResponse.workbook.counts?.open_questions || 0} open questions across{' '}
                {parseResponse.workbook.counts?.register_rows || 0} POs.
              </div>
            )}
            {parseResponse.workbook.source_filename && !parseResponse.workbook.present && (
              <Alert tone="error" variant="text" className="w-full !text-xs !leading-4">
                {parseResponse.workbook.source_filename} could not be read as a corrections book
                {parseResponse.workbook.error ? `: ${parseResponse.workbook.error.message}` : ''}.
              </Alert>
            )}
            {notImportedDocs.length > 0 && (
              <div className="w-full">
                <button
                  type="button"
                  onClick={() => setShowNotImported((v) => !v)}
                  className="text-xs text-on-surface-variant hover:text-on-surface"
                >
                  {showNotImported ? 'Hide' : 'Show'} {notImportedDocs.length} document
                  {notImportedDocs.length === 1 ? '' : 's'} not imported ▾
                </button>
                <Reveal show={showNotImported}>
                  <ul className="mt-2 space-y-1 text-xs">
                    {notImportedDocs.map((d) => (
                      <li key={d.source_filename} className="text-on-surface-variant">
                        <span className="font-mono text-on-surface-variant">{d.source_filename}</span>:{' '}
                        {d.error?.message || 'Not a purchase order.'}
                      </li>
                    ))}
                  </ul>
                </Reveal>
              </div>
            )}
          </div>
          </Panel>

          {/* Vendor triage */}
          <div ref={vendorTriageRef}>
            <PoImportVendorTriage
              groups={groups}
              resolutions={resolvedResolutions}
              onChange={handleChangeResolution}
              vendorTypes={vendorTypes}
              vendorList={vendorList}
              onPreviewVendor={setPreviewSubject}
            />
          </div>

          {/* Confirm results */}
          <Reveal show={!!confirmResult}>
          {confirmResult && (
            <Alert
              tone="success"
              title={(
                <>
                  Imported {confirmResult.created.length} purchase order{confirmResult.created.length === 1 ? '' : 's'}
                  {confirmResult.skipped ? `, skipped ${confirmResult.skipped} already-existing` : ''}
                  {confirmResult.attached ? `, attached ${confirmResult.attached} line${confirmResult.attached === 1 ? '' : 's'}` : ''}.
                </>
              )}
              action={(
                <button
                  type="button"
                  onClick={handleNextBatch}
                  className={buttonClass('filled')}
                >
                  Start next batch
                </button>
              )}
            >
              {confirmResult.created.length > 0 && (
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
                  {confirmResult.created.slice(0, 10).map((c) => (
                    <Link
                      key={c.purchase_order_id}
                      to={`/procurement/po/${c.purchase_order_id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="font-mono text-primary hover:underline"
                    >
                      {c.po_number}
                    </Link>
                  ))}
                  {confirmResult.created.length > 10 && (
                    <span className="text-on-surface-variant">+{confirmResult.created.length - 10} more</span>
                  )}
                </div>
              )}
              {droppedAtConfirm > 0 && (
                <p className="mt-2 text-xs text-on-surface-variant">
                  {droppedAtConfirm} document{droppedAtConfirm === 1 ? '' : 's'} in this batch{' '}
                  {droppedAtConfirm === 1 ? 'was' : 'were'} left unchecked and not confirmed. They are still below if
                  you want to finish them, or re-upload their files later.
                </p>
              )}
            </Alert>
          )}
          </Reveal>

          {/* Review list */}
          {draftList.length > 0 ? (
            <Panel
              padding="none"
              title="Documents"
              actions={(
                <SegTrack
                  label="Which documents to show"
                  size="sm"
                  value={filter}
                  onChange={(key) => setFilter(key as 'all' | 'attention' | 'warnings')}
                  options={[
                    { key: 'all', label: 'All' },
                    { key: 'attention', label: 'Needs attention' },
                    { key: 'warnings', label: 'Warnings' },
                  ]}
                />
              )}
              className="overflow-hidden"
            >
              <div className="divide-y divide-gray-700/40">
                {filteredList.length === 0 ? (
                  <EmptyState variant="area">Nothing matches this filter.</EmptyState>
                ) : (
                  filteredList.map((d) => (
                    <PoImportDocumentCard
                      key={d.key}
                      draft={d}
                      expanded={expanded.has(d.key)}
                      onToggleExpand={() => toggleExpand(d.key)}
                      onPatch={(patch) => patchDraft(d.key, patch)}
                      onPatchLine={(idx, patch) => patchLine(d.key, idx, patch)}
                      effectiveVendor={effectiveVendorByKey.get(d.key)!}
                      blockingReasons={blockingByKey.get(d.key) || []}
                      localInstance={parseResponse.instance}
                      vendorList={vendorList}
                      onJumpToVendorPanel={jumpToVendorPanel}
                      onPreviewVendor={setPreviewSubject}
                      documentFile={filesByName.get(d.original.source_filename) ?? null}
                    />
                  ))
                )}
              </div>
            </Panel>
          ) : (
            !confirmResult && (
              <EmptyState variant="area">No importable documents in this batch.</EmptyState>
            )
          )}

          {/* Batch status bar */}
          <Appear as="div" show={draftList.length > 0} className="sticky bottom-4 flex items-center gap-3 bg-surface-container-high rounded-md px-4 py-3 shadow-lg">
              <span className="text-sm text-on-surface tabular-nums">
                {readyToConfirmCount} of {includedDrafts.length} checked ready to confirm
              </span>
              <Appear show={awaitingReviewCount > 0} className="text-xs text-amber-300 tabular-nums">
                {awaitingReviewCount} waiting on AI reconciliation
              </Appear>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={confirming || includedDrafts.length === 0}
                className={`${buttonClass('filled')} ml-auto`}
              >
                {confirming ? 'Confirming…' : `Confirm ${includedDrafts.length} document${includedDrafts.length === 1 ? '' : 's'}`}
              </button>
              <button
                type="button"
                onClick={handleNextBatch}
                disabled={confirming}
                className={buttonClass('quiet')}
              >
                Start over
              </button>
          </Appear>
        </FadeIn>
      )}

      {previewSubject && <VendorPreviewModal subject={previewSubject} onClose={() => setPreviewSubject(null)} />}
    </PageLayout>
  );
};

// ---------------------------------------------------------------------------
// Upload step
// ---------------------------------------------------------------------------

const UploadStep: React.FC<{
  files: File[];
  workbookFile: File | null;
  parsing: boolean;
  onAddFiles: (files: FileList | File[]) => void;
  onRemoveFile: (name: string) => void;
  onClearFiles: () => void;
  onSetWorkbook: (f: File | null) => void;
  onParse: () => void;
}> = ({ files, workbookFile, parsing, onAddFiles, onRemoveFile, onClearFiles, onSetWorkbook, onParse }) => {
  const [showFiles, setShowFiles] = useState(false);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <DropZone
          what="up to 60 purchase-order .xlsx or .pdf files"
          accept=".xlsx,.pdf"
          multiple
          chosen={files}
          onFiles={onAddFiles}
        />

        <DropZone
          what="the corrections book"
          hint="Optional but recommended. The reconciliation .xlsx."
          accept=".xlsx"
          chosen={workbookFile}
          onFiles={(picked) => onSetWorkbook(picked[0] || null)}
        />
      </div>

      {files.length > 0 && (
        <div className="text-xs">
          <button type="button" onClick={() => setShowFiles((v) => !v)} className="text-on-surface-variant hover:text-on-surface">
            {showFiles ? 'Hide' : 'Show'} file list ▾
          </button>
          <button type="button" onClick={onClearFiles} className={`${buttonClass('quiet', 'sm')} ml-3`}>
            Clear all
          </button>
          <Reveal show={showFiles}>
            <ul className="mt-2 max-h-48 overflow-y-auto bg-surface-container-low rounded divide-y divide-outline-variant">
              {files.map((f) => (
                <li key={`${f.name}:${f.size}`} className="flex items-center justify-between px-3 py-1.5">
                  <span className="font-mono text-on-surface-variant truncate">{f.name}</span>
                  <Tooltip label={`Remove ${f.name} from the import`}>
                    <IconButton size="sm" label={`Remove ${f.name}`} onClick={() => onRemoveFile(f.name)} className="ml-2">
                      <IconClose />
                    </IconButton>
                  </Tooltip>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      )}

      <button
        type="button"
        onClick={onParse}
        disabled={files.length === 0 || parsing}
        aria-busy={parsing || undefined}
        className={buttonClass('filled')}
      >
        <Appear show={parsing}><LogoSpinner size="inline" /></Appear>
        {parsing ? 'Reading documents…' : `Read ${files.length || ''} document${files.length === 1 ? '' : 's'}`}
      </button>
    </div>
  );
};

export default PoImportReview;
